import useEavModels from "@/Hooks/useEavModels";
import { useIsMobile } from "@/Hooks/useIsMobile";
import useSelectionProperties from "@/Hooks/useSelectionProperties";
import useViewerStore from "@/stores/useViewerStore";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AutoSizer from "react-virtualized-auto-sizer";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";
import { XktContext } from "../../Context/XktContext/XktContextProvider";
import { Separator } from "../ui/separator";
import PropertyGroup from "./components/PropertyGroup";
import PropertySearchBar from "./components/PropertySearchBar";
import PropertyWindowHeader from "./components/PropertyWindowHeader";

const PropertyWindow = () => {
    const { togglePropertiesPanel } = useViewerStore();
    const { state: xktState } = useContext(XktContext);
    const { state: viewerState } = useContext(ViewerContext);
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [openSets, setOpenSets] = useState<string[]>([]);
    const [openProps, setOpenProps] = useState<string[]>([]);

    // Debounce
    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(searchInput), 200);
        return () => clearTimeout(h);
    }, [searchInput]);

    const objects = useEavModels(xktState.models);

    const { properties: computedProps, errorMessage } = useSelectionProperties({
        selectedEntities: viewerState.selectedEntities as string[],
        viewer: xktState.viewer,
        treeViewCache: viewerState.treeViewCache ?? [],
        objects,
        search: debouncedSearch,
        t,
    });

    const toggleSet = (name: string) =>
        setOpenSets((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));

    const toggleProp = (name: string) =>
        setOpenProps((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));

    return (
        <>
            <PropertyWindowHeader title={t("propWinMainTitle")} onClose={togglePropertiesPanel} />

            <PropertySearchBar
                value={searchInput}
                placeholder={t("propWinSearchPlaceholder")}
                onChange={setSearchInput}
                onClear={() => setSearchInput("")}
            />

            <Separator className="mt-2" />
            {isMobile ? (
                <div style={{ height: "calc(60vh - 8px)" }}>
                    <AutoSizer disableWidth>
                        {({ height }) => (
                            <div className="w-full overflow-auto" style={{ height: height - 121 }}>
                                {errorMessage && <div className="text-center">{errorMessage}</div>}

                                {computedProps.map((group, idx) => (
                                    <div key={group.category}>
                                        {idx > 0 && <Separator />}
                                        <PropertyGroup
                                            group={group}
                                            debouncedSearch={debouncedSearch}
                                            isOpen={openSets.includes(group.category)}
                                            onToggle={() => toggleSet(group.category)}
                                            openProps={openProps}
                                            toggleProp={toggleProp}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </AutoSizer>
                </div>
            ) : (
                <AutoSizer disableWidth>
                    {({ height }) => (
                        <div className="w-full overflow-auto" style={{ height: height - 121 }}>
                            {errorMessage && <div className="text-center">{errorMessage}</div>}

                            {computedProps.map((group, idx) => (
                                <div key={group.category}>
                                    {idx > 0 && <Separator />}
                                    <PropertyGroup
                                        group={group}
                                        debouncedSearch={debouncedSearch}
                                        isOpen={openSets.includes(group.category)}
                                        onToggle={() => toggleSet(group.category)}
                                        openProps={openProps}
                                        toggleProp={toggleProp}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </AutoSizer>
            )}
        </>
    );
};

export default PropertyWindow;
